import { useEffect, useState } from "react";
import { Checkbox } from "@narmi/design_system";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { FILTER_CONFIG, FilterChipsType, FilterType } from "../filterConfig";
import styles from "../Filters.module.scss";
import { ActionBar, FilterChip } from ".";

type MultiSelectFilterType = FilterType & {
  filterName: FilterChipsType;
  filterOptions: readonly { key: string; label: string }[];
  label: string;
};

const MultiSelectFilter = ({
  filters,
  isActive,
  handleSetActive,
  handleSetFilter,
  handleToggleFilter,
  filterName,
  filterOptions,
  label,
}: MultiSelectFilterType) => {
  const features = useUserFeatures();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const config = FILTER_CONFIG[filterName];
  const {
    expedited_payments: expeditedPaymentsFeature,
    ach_payments_collect: achPaymentsCollectFeature,
    wires: domesticWiresFeature,
    international_wires: internationalWiresFeature,
    international_wires_consumer: internationalWiresConsumerFeature,
  } = features;

  if (config.type !== "multi-select") {
    return null;
  }

  const { queryKey } = config;
  const count = filters[queryKey]?.length || undefined;

  const visibleOptions = filterOptions.filter(({ key }) => {
    if (key === "debit") return achPaymentsCollectFeature;
    if (key === "fednow") return expeditedPaymentsFeature;
    if (key === "domestic_wire") return domesticWiresFeature;
    if (key === "international_wire") return internationalWiresFeature || internationalWiresConsumerFeature;
    return true;
  });

  useEffect(() => {
    const initialSelectedOptions = filters[queryKey] ?? [];
    setSelectedOptions(initialSelectedOptions);
  }, [filters, queryKey, isActive]);

  const handleToggleOption = (option: string) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((s) => s !== option)
        : [...prev, option]
    );
  };

  const handleToggleChip = () => {
    handleSetActive(isActive ? "" : filterName);
  };

  const handleApplyFilter = () => {
    handleSetFilter({
      ...filters,
      [queryKey]: selectedOptions,
    });
    handleToggleChip();
  };

  const handleRemoveFilter = () => {
    handleToggleFilter(filterName);
  };

  const renderDropdown = () => (
    <>
      <div className={styles.filterDropdownContentScrollable}>
        {visibleOptions.map(({ key, label: optionLabel }) => (
          <div key={key} className="padding--all--xs">
            <Checkbox
              label={optionLabel}
              name={key}
              checked={selectedOptions.includes(key)}
              onChange={() => handleToggleOption(key)}
              {...{
                onMouseDown: (e: React.MouseEvent<HTMLElement>) =>
                  e.preventDefault(),
              }}
            />
          </div>
        ))}
      </div>
      <ActionBar
        handleApplyFilter={handleApplyFilter}
        handleRemoveFilter={handleRemoveFilter}
      />
    </>
  );

  return (
    <FilterChip
      dropdownContent={renderDropdown()}
      isActive={isActive}
      handleToggleChip={handleToggleChip}
      label={label}
      count={count}
    />
  );
};

export default MultiSelectFilter;
